import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  IconButton,
  AppBar,
  Toolbar,
  Divider,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAuth } from '../contexts/AuthContext';
import { LandingLayout } from '../components/landing/LandingLayout';
import { Footer } from '../components/landing/Footer';

export default function FAQPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);

  const faqSections = [
    {
      title: 'Core Concepts',
      questions: [
        {
          question: 'What is a Study Loop?',
          answer: 'A Study Loop is a deck of interactive, custom-made cards you cycle through to strengthen your recall. By repeatedly reviewing the same set of concepts—especially the ones that give you trouble—you\'ll transform new information into long-term knowledge.'
        },
        {
          question: 'How do Review Loops differ from Study Loops?',
          answer: 'Review Loops are specialized loops that automatically gather the questions you\'ve missed or flagged for extra practice. While a Study Loop covers your entire deck, a Review Loop narrows the focus to your problem areas, helping you tackle the questions that need the most attention.'
        },
        {
          question: 'Why does BrainLoop emphasize "loops" instead of just decks or flashcards?',
          answer: 'We chose the term Loop to emphasize the cyclical nature of learning. Rather than flipping through a deck once and moving on, BrainLoop encourages continuous repetition—especially of the toughest concepts—until you truly master them.'
        }
      ]
    },
    {
      title: 'Learning Effectiveness',
      questions: [
        {
          question: 'How do Study Loops and Review Loops speed up learning?',
          answer: 'Spaced repetition and targeted practice are key. Study Loops help you cover all your material at well-timed intervals, while Review Loops ensure you spend extra time on the hardest questions. This two-pronged approach keeps you from wasting time on concepts you\'ve already nailed and zeroes in on what you need to improve.'
        },
        {
          question: 'How often should I loop through my cards?',
          answer: 'That depends on the complexity of the subject and your personal goals. BrainLoop can guide you with a recommended schedule. Consistency is crucial: the more regularly you loop (and re-loop for trickier material), the faster your progress will be.'
        }
      ]
    },
    {
      title: 'Getting Started',
      questions: [
        {
          question: 'Can I create my own Study Loops and Review Loops?',
          answer: 'Absolutely! BrainLoop lets you design loops for any topic. From test prep to personal enrichment, you can load up your cards with text prompts, images, formulas—whatever helps you learn best. Whenever you miss a question, BrainLoop automatically places it into a Review Loop for you. You can also manually add items if you want to review them more often.'
        },
        {
          question: 'Do I need any special devices or software to use BrainLoop?',
          answer: 'Nope. BrainLoop works on any smartphone, tablet, or desktop. Whenever inspiration (or a spare moment) strikes, you can dive right back into your loops and keep the learning cycle going.'
        }
      ]
    },
    {
      title: 'Use Cases & Pricing',
      questions: [
        {
          question: 'Is BrainLoop only for test prep?',
          answer: 'Not at all! While it\'s fantastic for class assignments and high-stakes exams, BrainLoop is equally helpful for learning new languages, getting certified in specialized fields, or even studying for a passion project. Any concept you need to remember can be made into a loop.'
        },
        {
          question: 'Is BrainLoop free or paid?',
          answer: 'We offer a free tier that includes core features so anyone can get started. For advanced scheduling options, in-depth analytics, and other premium tools, we have a paid subscription option.'
        }
      ]
    }
  ];

  const handleBack = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqSections.flatMap(section =>
      section.questions.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    )
  };

  const FAQContent = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const clayCard = {
      borderRadius: '20px',
      border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.09) : alpha(theme.palette.primary.main, 0.1)}`,
      overflow: 'hidden',
      boxShadow: isDark
        ? [
            '0 14px 36px rgba(0,0,0,0.45)',
            '0 4px 10px rgba(0,0,0,0.28)',
            'inset 0 2px 0 rgba(255,255,255,0.05)',
            'inset 0 -2px 0 rgba(0,0,0,0.18)',
          ].join(', ')
        : [
            '0 14px 36px rgba(99,102,241,0.12)',
            '0 4px 10px rgba(0,0,0,0.06)',
            'inset 0 2px 0 rgba(255,255,255,0.95)',
            'inset 0 -2px 0 rgba(99,102,241,0.07)',
          ].join(', '),
      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
      '&:hover': {
        transform: 'translateY(-4px) scale(1.005)',
        boxShadow: isDark
          ? [
              '0 20px 48px rgba(0,0,0,0.52)',
              '0 8px 16px rgba(0,0,0,0.32)',
              'inset 0 2px 0 rgba(255,255,255,0.06)',
              'inset 0 -2px 0 rgba(0,0,0,0.2)',
            ].join(', ')
          : [
              '0 20px 48px rgba(99,102,241,0.18)',
              '0 8px 16px rgba(0,0,0,0.08)',
              'inset 0 2px 0 rgba(255,255,255,1)',
              'inset 0 -2px 0 rgba(99,102,241,0.1)',
            ].join(', '),
      },
    };

    return (
      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
        <Helmet>
          <title>BrainLoop FAQ - Common Questions About Study Loops & AI Flashcards</title>
          <meta name="description" content="Find answers to common questions about BrainLoop's study loops, AI flashcard generation, spaced repetition, pricing, and how to get started." />
          <link rel="canonical" href="https://brainloop.app/faq" />
          <meta property="og:title" content="BrainLoop FAQ - Common Questions About Study Loops & AI Flashcards" />
          <meta property="og:url" content="https://brainloop.app/faq" />
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        </Helmet>

        {/* Background depth blobs */}
        <Box sx={{
          position: 'absolute', top: -60, right: '8%',
          width: 420, height: 420, borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.082)}, transparent 65%)`,
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: 80, left: '5%',
          width: 340, height: 340, borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.063)}, transparent 65%)`,
          pointerEvents: 'none',
        }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>

          {/* Hero */}
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 10 } }}>
            {/* Big clay icon orb */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                mb: 3,
                background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                boxShadow: [
                  '0 14px 32px rgba(99,102,241,0.55)',
                  '0 5px 12px rgba(0,0,0,0.18)',
                  'inset 0 3px 0 rgba(255,255,255,0.32)',
                  'inset 0 -3px 0 rgba(0,0,0,0.18)',
                ].join(', '),
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 38, color: 'white' }} />
            </Box>

            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 800,
                letterSpacing: -0.5,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.5 }}>
              Find answers to common questions about BrainLoop
            </Typography>
          </Box>

          {/* Sections */}
          {faqSections.map((section, sectionIndex) => (
            <Box key={sectionIndex} sx={{ mb: { xs: 8, md: 10 } }}>

              {/* Section header pill + line */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 0.6,
                    borderRadius: '20px',
                    flexShrink: 0,
                    background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                    boxShadow: [
                      '0 5px 14px rgba(99,102,241,0.45)',
                      '0 2px 5px rgba(0,0,0,0.12)',
                      'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                      'inset 0 -1.5px 0 rgba(0,0,0,0.14)',
                    ].join(', '),
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: 'primary.contrastText', fontWeight: 700, letterSpacing: 1.5, fontSize: 'caption.fontSize' }}
                  >
                    {section.title}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>

              {/* Q&A cards */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {section.questions.map((item, index) => (
                  <Box
                    key={index}
                    component="article"
                    sx={{
                      bgcolor: 'background.paper',
                      ...clayCard,
                    }}
                  >
                    {/* Question row */}
                    <Box
                      sx={{
                        px: { xs: 3, md: 4 },
                        pt: { xs: 3, md: 3.5 },
                        pb: 2.5,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                      }}
                    >
                      {/* "Q" clay orb */}
                      <Box
                        sx={{
                          minWidth: 30,
                          height: 30,
                          borderRadius: '50%',
                          flexShrink: 0,
                          mt: 0.2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                          boxShadow: [
                            '0 4px 10px rgba(99,102,241,0.45)',
                            '0 1px 4px rgba(0,0,0,0.15)',
                            'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                            'inset 0 -1.5px 0 rgba(0,0,0,0.14)',
                          ].join(', '),
                        }}
                      >
                        <Typography sx={{ fontSize: 'caption.fontSize', fontWeight: 800, color: 'primary.contrastText', lineHeight: 1 }}>
                          Q
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ fontWeight: 700, lineHeight: 1.4 }}
                      >
                        {item.question}
                      </Typography>
                    </Box>

                    <Divider />

                    {/* Answer row */}
                    <Box
                      sx={{
                        px: { xs: 3, md: 4 },
                        pt: 2.5,
                        pb: { xs: 3, md: 3.5 },
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        bgcolor: isDark ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.primary.main, 0.03),
                      }}
                    >
                      {/* "A" clay orb */}
                      <Box
                        sx={{
                          minWidth: 30,
                          height: 30,
                          borderRadius: '50%',
                          flexShrink: 0,
                          mt: 0.2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.8)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
                          boxShadow: [
                            '0 3px 8px rgba(99,102,241,0.3)',
                            '0 1px 3px rgba(0,0,0,0.1)',
                            'inset 0 1.5px 0 rgba(255,255,255,0.22)',
                            'inset 0 -1.5px 0 rgba(0,0,0,0.1)',
                          ].join(', '),
                        }}
                      >
                        <Typography sx={{ fontSize: 'caption.fontSize', fontWeight: 800, color: 'primary.contrastText', lineHeight: 1 }}>
                          A
                        </Typography>
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        {item.answer}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Container>
      </Box>
    );
  };

  if (isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="fixed" color="default" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider', backdropFilter: 'blur(8px)' }}>
          <Toolbar>
            <IconButton edge="start" onClick={handleBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              FAQ
            </Typography>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ pt: 10 }}>
          <FAQContent />
        </Box>
      </Box>
    );
  }

  return (
    <LandingLayout>
      <FAQContent />
      <Footer />
    </LandingLayout>
  );
}
